import type Controller from '@ember/controller';
import { inject as controller } from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismBreadcrumb,
  PrismCard,
  PrismMessage,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import SettingsPageTemplate from 'lumen/components/settings/page-template';
import SettingsRecommendationsCreateSeasonalStrategyButton from 'lumen/components/settings/recommendations/create-seasonal-strategy-button';
import MultiRoomPriceRangeStrategyTable from 'lumen/components/settings/recommendations/multi-room-price-range-strategy-table';
import SettingsRecommendationsPriceRangeStrategyTable from 'lumen/components/settings/recommendations/price-range-strategy-table';
import SettingsRecommendationsStrategyCard from 'lumen/components/settings/recommendations/strategy-card';
import type { RecommendationStrategy } from 'lumen/models';
import type { RecommendationsModel } from 'lumen/routes/hotel/settings/recommendations';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { sortSeasonalStrategies } from 'lumen/utils/pricing-assistant/recommendation-settings';

interface SettingsRecommendationsPriceRangeSignature {
  Args: {
    model: RecommendationsModel;
  };
}

export default class SettingsRecommendationsPriceRange extends Component<SettingsRecommendationsPriceRangeSignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare pricingAssistantPmsSs: Services['pricing-assistant-pms-ss'];

  @controller('hotel.settings.recommendations')
  declare recommendationsController: Controller & {
    strategiesPerType: { priceRangeStrategies: RecommendationStrategy[] };
  };

  @tracked isSomeStrategyCardBeingEdited = false;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get defaultStrategy() {
    // There should be only one default strategy
    return this.priceRangeStrategies.find(
      (strategy) => strategy.isDefaultStrategy,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get pageDescription() {
    if (this.access.hasPricingAssistantOpenPricingFeature) {
      return this.intl.t(
        'pricing_assistant.recommendations.price_range.multi-room.explanation',
      );
    }

    if (this.pricingAssistantPmsSs.isPricingAssistantPMSIntegrated) {
      return this.intl.t(
        'pricing_assistant.recommendations.price_range.explanation.integrated',
        {
          htmlSafe: true,
          pmsLeadingRoomClass: 'text-300--bold',
          pmsLeadingRoomName: this.pmsLeadingRoomName,
        },
      );
    }
    return this.intl.t(
      'pricing_assistant.recommendations.price_range.explanation.non_integrated',
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get pageExtraInfo() {
    if (this.pricingAssistantPmsSs.isPricingAssistantPMSIntegrated) {
      const hasOffsetValue =
        this.hotelAndMarketService.hotel.caxxxPmsSettings.pms_root_rate_offset
          .value !== 0;

      if (hasOffsetValue) {
        return this.intl.t(
          'pricing_assistant.recommendations.settings.pricing_range.description.integrated_with_offset',
        );
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get pmsLeadingRoomName() {
    return this.hotelAndMarketService.hotel.caxxxPmsSettings
      .pms_id_to_booking_room_name_mapping[
      this.hotelAndMarketService.hotel.caxxxPmsSettings.pms_leading_room_id
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get priceRangeStrategies() {
    return this.recommendationsController.strategiesPerType
      .priceRangeStrategies;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get roomOffsetHierarchy() {
    return this.args.model.roomOffsetHierarchy;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get seasonalStrategies() {
    return sortSeasonalStrategies(this.priceRangeStrategies);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get strategyComponent() {
    return this.access.hasPricingAssistantOpenPricingFeature
      ? MultiRoomPriceRangeStrategyTable
      : SettingsRecommendationsPriceRangeStrategyTable;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get title() {
    if (this.access.hasPricingAssistantOpenPricingFeature) {
      return this.intl.t(
        'pricing_assistant.recommendations.settings.pricing_range.multi_room.title',
      );
    }

    return this.intl.t(
      'pricing_assistant.recommendations.settings.pricing_range.title',
    );
  }

  @action setSomeStrategyCardBeingEditedState(state: boolean): void {
    this.isSomeStrategyCardBeingEdited = state;
  }

  <template>
    <SettingsPageTemplate
      @description={{this.pageDescription}}
      @extraInfo={{this.pageExtraInfo}}
      @title={{this.title}}
      @type="wide-container"
    >
      <:breadcrumb>
        <PrismBreadcrumb as |B|>
          <B.item
            @label={{t "pricing_assistant.recommendations.settings.main_title"}}
            @route="hotel.settings.recommendations.index"
          />
          <B.item @isCurrent={{true}} @label={{this.title}} />
        </PrismBreadcrumb>
      </:breadcrumb>
      <:link>
        <a
          class="prism-link"
          href="https://help.mylighthouse.com/en/articles/7857322-how-do-i-customize-my-recommendations"
          rel="noopener noreferrer"
          target="_blank"
        >{{t "general.learn_more"}}</a>
      </:link>
      <:default>
        <div class="seasonal-settings-wrapper">
          {{#if this.defaultStrategy}}
            <div class="pb-400">
              <SettingsRecommendationsStrategyCard
                @initiallyOpened={{true}}
                @isSomeStrategyCardBeingEdited={{this.isSomeStrategyCardBeingEdited}}
                @pmsLeadingRoomName={{this.pmsLeadingRoomName}}
                @roomOffsetHierarchy={{this.roomOffsetHierarchy}}
                @saveStrategyTask={{@model.saveStrategyTask}}
                @seasonalStrategies={{this.seasonalStrategies}}
                @setSomeStrategyCardBeingEditedState={{this.setSomeStrategyCardBeingEditedState}}
                @strategy={{this.defaultStrategy}}
                @strategyComponent={{this.strategyComponent}}
              />
            </div>
          {{/if}}

          {{#if this.seasonalStrategies.length}}
            <div class="flex flex-col gap-200">
              <div class="flex flex-row items-center justify-between">
                <h3 class="text-neutral-emphasis">{{t
                    "pricing_assistant.recommendations.price_range.subtitle.seasonal_strategies"
                  }}</h3>

                {{#unless
                  this.access.isPricingAssistantRecommendationSettingsActionOrInputDisabled
                }}
                  <SettingsRecommendationsCreateSeasonalStrategyButton
                    @defaultStrategy={{this.defaultStrategy}}
                    @isSomeStrategyCardBeingEdited={{this.isSomeStrategyCardBeingEdited}}
                    @seasonalStrategies={{this.seasonalStrategies}}
                    @setSomeStrategyCardBeingEditedState={{this.setSomeStrategyCardBeingEditedState}}
                    @settingsType="price_range"
                  />
                {{/unless}}
              </div>
              {{#each this.seasonalStrategies as |strategy|}}
                <SettingsRecommendationsStrategyCard
                  @defaultStrategy={{this.defaultStrategy}}
                  @isSomeStrategyCardBeingEdited={{this.isSomeStrategyCardBeingEdited}}
                  @roomOffsetHierarchy={{this.roomOffsetHierarchy}}
                  @saveStrategyTask={{@model.saveStrategyTask}}
                  @seasonalStrategies={{this.seasonalStrategies}}
                  @setSomeStrategyCardBeingEditedState={{this.setSomeStrategyCardBeingEditedState}}
                  @strategy={{strategy}}
                  @strategyComponent={{this.strategyComponent}}
                />
              {{/each}}
            </div>
          {{else}}
            {{#unless
              this.access.isPricingAssistantRecommendationSettingsActionOrInputDisabled
            }}
              <PrismCard class="flex items-center justify-center pt-400">
                <:content>
                  <PrismMessage
                    @description={{t
                      "pricing_assistant.recommendations.price_range.empty_state.description"
                    }}
                    @illustration="100x100_chart"
                    @title={{t
                      "pricing_assistant.recommendations.empty_state.title"
                    }}
                  >
                    <:primaryAction>
                      <SettingsRecommendationsCreateSeasonalStrategyButton
                        @defaultStrategy={{this.defaultStrategy}}
                        @isSomeStrategyCardBeingEdited={{this.isSomeStrategyCardBeingEdited}}
                        @seasonalStrategies={{this.seasonalStrategies}}
                        @setSomeStrategyCardBeingEditedState={{this.setSomeStrategyCardBeingEditedState}}
                        @settingsType="price_range"
                      />
                    </:primaryAction>
                  </PrismMessage>
                </:content>
              </PrismCard>
            {{/unless}}
          {{/if}}
        </div>
      </:default>
    </SettingsPageTemplate>
  </template>
}
